// src/services/verseInteractionService.js
import { supabase } from '../lib/supabase';

export const verseInteractionService = {
  /**
   * Get verse interaction data for a specific chapter
   * @param {string} book - Book name (e.g., "Genesis")
   * @param {number} chapter - Chapter number
   * @param {string} versionId - Bible version ID (optional)
   * @returns {Promise<Object>} Verse interactions data
   */
  async getChapterInteractions(book, chapter, versionId = null) {
    try {
      const { data, error } = await supabase?.rpc('get_chapter_verse_interactions', {
        book_name: book,
        chapter_num: chapter,
        version_id_param: versionId
      });

      if (error) {
        console.error('Error fetching chapter interactions:', error);
        throw error;
      }

      // Convert to a map for easy lookup by verse number
      const interactionsMap = new Map();
      
      data?.forEach(item => {
        interactionsMap?.set(item?.verse_number, {
          bibleVerseId: item?.bible_verse_id,
          verseNumber: item?.verse_number,
          reflectionCount: item?.reflection_count,
          libraryCount: item?.library_count,
          publicReflections: item?.public_reflections || []
        });
      });

      return { data: interactionsMap, error: null };
    } catch (err) {
      console.error('Network error fetching verse interactions:', err);
      return { data: new Map(), error: err };
    }
  },

  /**
   * Get public reflections for a specific Bible verse
   * @param {number} bibleVerseId - Bible verse ID
   * @returns {Promise<Array>} Array of public reflections
   */
  async getPublicReflections(bibleVerseId) {
    try {
      // Get verse interaction data which includes public reflections
      const { data, error } = await supabase?.from('verse_interactions')?.select(`
          bible_verse_id,
          reflection_count,
          library_count
        `)?.eq('bible_verse_id', bibleVerseId)?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      // If no interaction record exists, return empty array
      if (!data) {
        return { data: [], error: null };
      }

      // Get public reflections through the database function for better performance
      const { data: reflectionsData, error: reflectionsError } = await supabase?.rpc(
        'get_chapter_verse_interactions',
        {
          // We need to get the book and chapter from the bible_verse_id
          // This is a workaround since we need more specific function
          book_name: '', // Will be filled by a more specific query if needed
          chapter_num: 0
        }
      );

      // For now, return empty array and let the component use the data from chapter interactions
      return { data: [], error: null };
    } catch (err) {
      console.error('Error fetching public reflections:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Trigger recalculation of verse interaction counts
   * @param {number} bibleVerseId - Bible verse ID
   * @returns {Promise<Object>} Success status
   */
  async refreshInteractionCounts(bibleVerseId) {
    try {
      // Update both reflection and library counts
      await supabase?.rpc('update_verse_reflection_count', {
        bible_verse_id_param: bibleVerseId
      });

      await supabase?.rpc('update_verse_library_count', {
        bible_verse_id_param: bibleVerseId
      });

      return { success: true, error: null };
    } catch (err) {
      console.error('Error refreshing interaction counts:', err);
      return { success: false, error: err };
    }
  },

  /**
   * Get users who have engaged with verses in a chapter (reflections + memorization)
   * @param {string} book - Book name (e.g., "Genesis")
   * @param {number} chapter - Chapter number
   * @param {string} versionId - Bible version ID (optional)
   * @returns {Promise<Object>} Map of verse numbers to engagement data
   */
  async getVerseEngagementUsers(book, chapter, versionId = null) {
    try {
      const { data, error } = await supabase?.rpc('get_verse_engagement_users', {
        book_name: book,
        chapter_num: chapter,
        version_id_param: versionId
      });

      if (error) {
        console.error('Error fetching verse engagement users:', error);
        throw error;
      }

      // Group by verse number and aggregate users
      const engagementMap = new Map();
      
      data?.forEach(item => {
        if (!engagementMap?.has(item?.verse_number)) {
          engagementMap?.set(item?.verse_number, {
            verseNumber: item?.verse_number,
            bibleVerseId: item?.bible_verse_id,
            users: []
          });
        }
        
        const verseData = engagementMap?.get(item?.verse_number);
        
        // Avoid duplicate users (same user may have both reflected and memorized)
        const existingUser = verseData?.users?.find(u => u?.userId === item?.user_id);
        
        if (!existingUser) {
          verseData?.users?.push({
            userId: item?.user_id,
            username: item?.username,
            avatarSvg: item?.avatar_svg,
            engagementTypes: [item?.engagement_type],
            createdAt: item?.created_at
          });
        } else {
          // Add engagement type if not already present
          if (!existingUser?.engagementTypes?.includes(item?.engagement_type)) {
            existingUser?.engagementTypes?.push(item?.engagement_type);
          }
        }
      });

      // Sort users by most recent interaction first
      engagementMap?.forEach(verseData => {
        verseData?.users?.sort((a, b) => 
          new Date(b?.createdAt) - new Date(a?.createdAt)
        );
      });

      return { data: engagementMap, error: null };
    } catch (err) {
      console.error('Network error fetching verse engagement users:', err);
      return { data: new Map(), error: err };
    }
  }
};